import React, { useState } from 'react';
import { BadgeCheckIcon, BriefcaseIcon, FileTextIcon, MapPinIcon, TriangleAlertIcon } from 'lucide-react';
import { DomainHeader } from '../../components/ui/DomainHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Meter } from '../../components/ui/Meter';
import { ChoiceGroup } from '../../components/ui/ChoiceGroup';
import { EmptyState } from '../../components/ui/EmptyState';
import { ReportControls } from '../../components/trust/ReportControls';
import { RecommendationPanel } from '../../components/intelligence/RecommendationPanel';
import { useAccount } from '../../contexts/AccountContext';
import { recommendRoles } from '../../utils/recommend';
import { careerProfile, jobApplications, opportunities } from '../../data/career';
import { PIPELINE } from '../../types/employer';
import type { PipelineStage } from '../../types/employer';
const FILTERS = ['All', 'Internship', 'Graduate role', 'Part-time', 'Scholarship'] as const;

/** Student-facing stages map onto the employer pipeline so both sides agree. */
const STAGE_MAP: Record<string, PipelineStage> = {
  Submitted: 'Applied',
  'In review': 'Screening',
  Shortlisted: 'Shortlisted',
  Interview: 'Interview',
  Offer: 'Offer'
};
export function Career() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');
  const [saved, setSaved] = useState<string[]>([]);
  const {
    signals
  } = useAccount();
  const roleSuggestions = recommendRoles(signals);
  const visible = opportunities.filter(opportunity => filter === 'All' || opportunity.kind === filter);
  return <div className="space-y-6" data-id="element-10584" data-mp="154xwjw12bxyg">
      <DomainHeader domain="work" eyebrow="Career" title="Opportunities and applications" description="Internships, graduate roles, scholarships and part-time work. Verified employers are checked by Campuzon; everything else is clearly marked." data-id="element-10585" data-mp="153wakwiidr1y" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-10586" data-mp="1veey5cbqd2ox">
        <div className="space-y-4" data-id="element-10587" data-mp="1qfv7iym6z2q8">
          <ChoiceGroup options={FILTERS} value={filter} onChange={setFilter} size="sm" data-id="element-10588" data-mp="1qhoj8sbnwd53" />

          {visible.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-10589" data-mp="1lnk6ttjmncwm">
              <EmptyState icon={<BriefcaseIcon className="h-4 w-4" data-id="element-10591" data-mp="1hm8p0a6zvvyz" />} title="Nothing listed yet" description="No opportunities of this type are open right now. We will notify you when new ones are posted for your programme." data-id="element-10590" data-mp="1hnjtydc6f9ro" />
            </div> : <ul className="space-y-3" data-id="element-10592" data-mp="12svimik3hh45">
              {visible.map(opportunity => <li key={opportunity.id} className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-10593" data-mp="yr0el6as4pmo">
                  <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-10594" data-mp="2t2yqxh4dy1ms">
                    <div className="min-w-0" data-id="element-10595" data-mp="evz7xbnvb8ai">
                      <h2 className="text-[15px] font-semibold text-ink" data-id="element-10596" data-mp="ev7advapn0hl">{opportunity.title}</h2>
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-[13px] text-ink-muted" data-id="element-10597" data-mp="jspl9r8afps8">
                        <span className="font-medium text-ink" data-id="element-10598" data-mp="om26rsho7ggr">{opportunity.employer}</span>
                        {opportunity.employerVerified ? <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-10600" data-mp="3s3anua4kqcaf" />} data-id="element-10599" data-mp="3s2ivd869wuod">
                            Verified employer
                          </Badge> : <Badge tone="warn" icon={<TriangleAlertIcon className="h-3.5 w-3.5" data-id="element-10602" data-mp="4whyqk9eqc9s" />} data-id="element-10601" data-mp="4x05wbxlyawh">
                            Unverified employer
                          </Badge>}
                      </p>
                      <p className="cz-num mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-muted" data-id="element-10603" data-mp="1gbwvpo1m944w">
                        <span className="inline-flex items-center gap-1.5" data-id="element-10604" data-mp="1ldm7x70krnuk">
                          <MapPinIcon className="h-3.5 w-3.5" data-id="element-10605" data-mp="1juw0a7y2gigo" />
                          {opportunity.location} · {opportunity.arrangement}
                        </span>
                        <span data-id="element-10606" data-mp="1svanjculc7mt">{opportunity.stipend}</span>
                        <span data-id="element-10607" data-mp="1sspo0uhrm3s8">{opportunity.deadlineLabel}</span>
                      </p>
                      <ul className="mt-2.5 flex flex-wrap gap-1.5" data-id="element-10608" data-mp="1ovjoymyton7q">
                        {opportunity.skills.map(skill => <li key={skill} data-id="element-10609" data-mp="1xunb5ztadiqg">
                            <Badge data-id="element-10610" data-mp="1xtly5pspmm63">{skill}</Badge>
                          </li>)}
                      </ul>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2" data-id="element-10611" data-mp="27jf0s1avjqy7">
                      <Button size="sm" variant="primary" data-id="element-10612" data-mp="27mayppmq2fmn">
                        Apply
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setSaved(current => current.includes(opportunity.id) ? current.filter(id => id !== opportunity.id) : [...current, opportunity.id])} data-id="element-10613" data-mp="2gkwpg82cl0yi">
                        {saved.includes(opportunity.id) ? 'Saved' : 'Save'}
                      </Button>
                    </div>
                  </div>
                  {!opportunity.employerVerified && <p className="mt-3 rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-[12px] leading-5 text-ink-muted" data-id="element-10614" data-mp="27hrajnwmthij">
                      This employer has not completed verification. Never pay a fee to apply for a
                      role, and report anyone who asks you to.
                    </p>}
                  <div className="mt-3 flex items-center justify-end border-t border-line pt-3" data-id="element-10615" data-mp="23f4dn8l3t6eh">
                    <ReportControls subject={opportunity.title} surface="job" data-id="element-10616" data-mp="907f36q1xhi5" />
                  </div>
                </li>)}
            </ul>}
        </div>

        <div className="space-y-6" data-id="element-10617" data-mp="3vf0h4kclmrr5">
          <RecommendationPanel title="Matched to your profile" description="Ranked against the skills on your career profile." items={roleSuggestions.map(suggestion => ({
          id: suggestion.item.id,
          title: suggestion.item.title,
          meta: `${suggestion.item.employer} · ${suggestion.item.stipend}`,
          reason: suggestion.reason,
          fallback: suggestion.fallback
        }))} data-id="element-10618" data-mp="3vgbhea6u5hdi" />

          <Panel title="Career profile" data-id="element-10619" data-mp="dxz4nu384rj6">
            <Meter value={careerProfile.completeness} label="Profile completeness" trailing={`${careerProfile.completeness}%`} data-id="element-10620" data-mp="e14vpevey3hj" />
            <p className="mt-3 text-[13px] leading-6 text-ink-muted" data-id="element-10621" data-mp="2yny6dltoe4dc">
              {careerProfile.headline}
            </p>
            <div className="mt-4 border-t border-line pt-3" data-id="element-10622" data-mp="1e5e0ceifmna6">
              <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10623" data-mp="1j6tinyt86fov">
                Still missing
              </p>
              <ul className="mt-2 space-y-1.5" data-id="element-10624" data-mp="1rylq00myd8p8">
                {careerProfile.missing.map(item => <li key={item} className="text-[13px] text-ink-muted" data-id="element-10625" data-mp="1wxym7bzprs4h">
                    · {item}
                  </li>)}
              </ul>
            </div>
            <Button variant="secondary" className="mt-4 w-full" icon={<FileTextIcon className="h-4 w-4" data-id="element-10627" data-mp="26u1oqiyh9sry" />} data-id="element-10626" data-mp="21ymcfvg4c4xn">
              Edit CV — updated {careerProfile.cvUpdated}
            </Button>
          </Panel>

          <Panel title="Your applications" description="The same pipeline the employer is working — you see the stage they moved you to." bodyClassName="p-0" data-id="element-10628" data-mp="cj6elc7na5n2">
            <ul className="divide-y divide-line" data-id="element-10629" data-mp="3tr15fanp5rug">
              {jobApplications.map(application => {
              const current = STAGE_MAP[application.stage] ?? 'Applied';
              const reached = PIPELINE.indexOf(current);
              return <li key={application.id} className="px-5 py-3.5" data-id="element-10630" data-mp="2dodjljfqlnt">
                    <p className="text-[13px] font-medium text-ink" data-id="element-10631" data-mp="3atq83vlml8hn">{application.title}</p>
                    <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-10632" data-mp="3avkttc4q14ms">
                      {application.employer} · submitted {application.submitted}
                    </p>
                    <ol className="mt-2.5 flex items-center gap-1" data-id="element-10633" data-mp="3jvps8wrxssot">
                      {PIPELINE.map((stage, index) => <li key={stage} className="flex-1" data-id="element-10634" data-mp="z474ext5eaky" data-mpi={index}>
                          <span className={`block h-1 rounded-full ${index <= reached ? 'bg-brand-700' : 'bg-line'}`} aria-hidden="true" data-id="element-10635" data-mp="z2ma99aqwtnm" />
                        </li>)}
                    </ol>
                    <p className="mt-2 flex items-center justify-between gap-2" data-id="element-10636" data-mp="sogoria8bsdb">
                      <Badge tone={reached >= 3 ? 'ok' : 'brand'} data-id="element-10637" data-mp="xn0ezuvuvqbh">{current}</Badge>
                      <span className="cz-num text-[11px] text-ink-subtle" data-id="element-10638" data-mp="xouz56zazeei">
                        stage {reached + 1} of {PIPELINE.length}
                      </span>
                    </p>
                  </li>;
            })}
            </ul>
          </Panel>

          <Panel title="Skills on your profile" data-id="element-10639" data-mp="1bmj8pinx6z49">
            <ul className="flex flex-wrap gap-1.5" data-id="element-10640" data-mp="1glclt9zaoo3w">
              {careerProfile.skills.map(skill => <li key={skill} data-id="element-10641" data-mp="24b6xvpbsi8n3">
                  <Badge data-id="element-10642" data-mp="249vy00lgtl19">{skill}</Badge>
                </li>)}
            </ul>
          </Panel>
        </div>
      </div>
    </div>;
}